import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Alert,
  Link,
  Divider,
  Checkbox,
  FormControlLabel,
  Skeleton,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PublicIcon from '@mui/icons-material/Public';
import {
  formatUSD,
  META_PRICING_DOCS_URL,
} from '../../../apps/campaigns/constants';


export const PricingSummary = ({ pricing, loading, compact = false, termsAccepted = false, onAcceptTerms }) => {
  if (loading) {
    return (
      <Box sx={{ mt: 3 }}>
        <Skeleton variant="rounded" height={compact ? 60 : 200} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!pricing) {
    return null;
  }

  const { total_contacts, pricing: pricingData } = pricing;

  if (compact) {
    return (
        <>
            <Paper
                elevation={0}
                sx={{
                mt: 3,
                p: 2,
                backgroundColor: 'primary.lighter',
                border: '1px solid',
                borderColor: 'primary.light',
                borderRadius: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                <AttachMoneyIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600} color="primary.dark">
                    Costo Estimado de la Campaña (USD)
                </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="body2" color="text.secondary">
                    {total_contacts} contacto{total_contacts !== 1 ? 's' : ''}
                    </Typography>
                    {pricing.markets && Object.keys(pricing.markets).length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                        {Object.keys(pricing.markets).length} mercado{Object.keys(pricing.markets).length !== 1 ? 's' : ''}
                    </Typography>
                    )}
                </Box>

                <Typography variant="h6" fontWeight={700} color="primary.main">
                    {formatUSD(pricingData?.total_cost_usd || 0)}
                </Typography>
                </Box>

                
            </Paper>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
              sx={{ mt: 2 }}
            >
              *Podrás obtener mayor información de este costo en el apartado resumen.  
            </Typography>
        </>
    );
  }


  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: 'grey.50',
        borderRadius: 2,
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Cotización (USD)
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Box
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: 'white',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Total de Contactos
          </Typography>
          <Chip label={total_contacts} size="small" color="primary" />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" fontWeight={600}>
            Costo Total Estimado
          </Typography>
          <Typography variant="h6" fontWeight={700} color="primary.main">
            {formatUSD(pricingData?.total_cost_usd || 0)}
          </Typography>
        </Box>
      </Box>

      {/* card esglose por mercado */}
      {pricingData?.breakdown && Object.keys(pricingData.breakdown).length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
            Desglose por Mercado
          </Typography>

          {Object.entries(pricingData.breakdown).map(([market, details]) => (
            <Paper
              key={market}
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1,
                backgroundColor: 'white',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PublicIcon fontSize="small" color="action" />
                <Typography variant="body2" fontWeight={600}>
                  {market}
                </Typography>
                <Chip
                  label={`${details.contacts} contacto${details.contacts !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              </Box>

              <Box sx={{ pl: 3.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Tarifa WhatsApp
                  </Typography>
                  <Typography variant="caption">
                    {formatUSD(details.whatsapp_fee)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Tarifa Yupio
                  </Typography>
                  <Typography variant="caption">
                    {formatUSD(details.yupio_fee)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 0.5 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" fontWeight={600}>
                    Costo por mensaje
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatUSD(details.cost_per_message)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" fontWeight={600} color="primary.dark">
                    Total
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color="primary.main">
                    {formatUSD(details.total_cost)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <Alert severity="info" icon={false} sx={{ mt: 1 }}>
        <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
          *Los costos mostrados son aproximados y están sujetos a cambios según las políticas 
          de Meta. Los precios finales pueden variar dependiendo de las tarifas vigentes al 
          momento del envío. Para más información, consulta la documentación oficial de Meta.
        </Typography>
        <Link
          href={META_PRICING_DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          sx={{ fontWeight: 600 }}
        >
          Ver documentación de precios de Meta →
        </Link>
      </Alert>

      <FormControlLabel
        sx={{ mt: 2 }}
        control={
          <Checkbox
            size="small"
            checked={termsAccepted}
            onChange={(e) => onAcceptTerms && onAcceptTerms(e.target.checked)}
          />
        }
        label={
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            Acepto la cotización de la campaña, así como los términos y condiciones.
          </Typography>
        }
      />

    </Paper>
  );
};

PricingSummary.propTypes = {
  pricing: PropTypes.shape({
    template_type: PropTypes.string,
    upload_type: PropTypes.string,
    total_contacts: PropTypes.number,
    markets: PropTypes.object,
    pricing: PropTypes.shape({
      template_type: PropTypes.string,
      breakdown: PropTypes.object,
      total_cost_usd: PropTypes.number,
    }),
  }),
  loading: PropTypes.bool,
  compact: PropTypes.bool,
  termsAccepted: PropTypes.bool,
  onAcceptTerms: PropTypes.func,
};